import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { Printer, FileDown } from 'lucide-react';
import { exportCSV } from './reportUtils';
import { useCompanyInfo, generatePrintHTML, openPrint } from './ReportExporter';

export default function IFTAAuditReport() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(`Q${Math.ceil((new Date().getMonth() + 1) / 3)}`);
  const [driverFilter, setDriverFilter] = useState('');
  const [driverNameByEmail, setDriverNameByEmail] = useState({});
  const company = useCompanyInfo();

  useEffect(() => {
    client.entities.IFTAMileageLog.list('-trip_date', 1000).then(l => { setLogs(l); setLoading(false); });
    client.entities.DriverProfile.list('-updated_date', 500).then(profiles => {
      const map = {};
      for (const p of profiles) if (p.user_email) map[p.user_email.toLowerCase()] = p.full_name;
      setDriverNameByEmail(map);
    });
  }, []);

  const driverLabel = (email) => (email && driverNameByEmail[email.toLowerCase()]) || email || '';

  const filtered = logs.filter(l =>
    l.year === year &&
    l.quarter?.startsWith(quarter) &&
    (!driverFilter || l.driver_email === driverFilter)
  );

  const drivers = [...new Set(logs.map(l => l.driver_email).filter(Boolean))].sort();

  const rows = [];
  filtered.forEach(l => {
    try {
      const miles = JSON.parse(l.state_mileage || '[]');
      miles.forEach(({ state, miles: m }) => {
        rows.push({
          'Load #': l.load_number,
          Driver: driverLabel(l.driver_email),
          'Trip Date': l.trip_date,
          Origin: l.origin,
          Destination: l.destination,
          State: state,
          Miles: Math.round(parseFloat(m) || 0),
        });
      });
    } catch {}
  });

  const handlePrint = () => {
    const cols = ['Load #', 'Driver', 'Trip Date', 'Origin', 'Destination', 'State', 'Miles'];
    const dataRows = rows.map(r => [
      { value: `#${r['Load #']}`, class: 'bold' }, r.Driver, r['Trip Date'], r.Origin, r.Destination,
      { value: r.State, class: 'bold' }, r.Miles.toLocaleString(),
    ]);
    const totalAuditMiles = rows.reduce((s, r) => s + r.Miles, 0);
    const cards = [
      { label: 'Log Entries', value: rows.length, color: '' },
      { label: 'States', value: [...new Set(rows.map(r => r.State))].length, color: 'gold' },
      { label: 'Total Miles', value: totalAuditMiles.toLocaleString(), color: 'positive' },
    ];
    const html = generatePrintHTML('IFTA Audit Report', `${quarter} ${year}`, cols, dataRows, null, company, cards);
    openPrint(html);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Quarter</label>
            <select value={quarter} onChange={e => setQuarter(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
              {['Q1', 'Q2', 'Q3', 'Q4'].map(q => <option key={q} value={q} className="bg-background">{q}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Year</label>
            <select value={year} onChange={e => setYear(Number(e.target.value))}
              className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
              {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y} className="bg-background">{y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Driver</label>
            <select value={driverFilter} onChange={e => setDriverFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-transparent border border-input text-foreground text-xs">
              <option value="" className="bg-background">All Drivers</option>
              {drivers.map(d => <option key={d} value={d} className="bg-background">{driverLabel(d)}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportCSV(rows, `ifta-audit-${quarter}-${year}.csv`)} disabled={rows.length === 0}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/20 border border-primary/40 text-primary text-xs font-semibold hover:bg-primary/30 transition-colors disabled:opacity-40">
            Export Audit CSV
          </button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><Printer className="w-4 h-4" /> Print</button>
          <button onClick={handlePrint} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-border text-sm text-foreground hover:bg-white/10"><FileDown className="w-4 h-4" /> Export PDF</button>
        </div>
      </div>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Load #', 'Driver', 'Trip Date', 'Origin', 'Destination', 'State', 'Miles'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-muted-foreground font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-white/3">
                <td className="px-3 py-2 text-primary">#{r['Load #']}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.Driver}</td>
                <td className="px-3 py-2 text-muted-foreground">{r['Trip Date']}</td>
                <td className="px-3 py-2 text-foreground">{r.Origin}</td>
                <td className="px-3 py-2 text-foreground">{r.Destination}</td>
                <td className="px-3 py-2 font-semibold text-foreground">{r.State}</td>
                <td className="px-3 py-2 text-foreground">{r.Miles.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && !loading && <p className="text-center text-muted-foreground py-8 text-sm">No audit data for {quarter} {year}.</p>}
        {loading && <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>}
      </div>
      <p className="text-xs text-muted-foreground">Showing per-load per-state mileage source data for audit purposes.</p>
    </div>
  );
}